import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IGetCalendarTripsResponse } from "../models/Trip";
import {
  IApplyOptimiseRequest,
  IApplyOptimiseResponse,
  IGeocodeLeadResponse,
  IOptimiseResultResponse,
  IOptimiseTripsRequest,
} from "../models/TripOptimisation";

export default function useTrips(year: number, week: number) {
  const { get, post, put, patch, del } = useApiHelper();
  const queryClient = useQueryClient();

  const trips = useQuery({
    queryKey: ["trips", year, week],
    queryFn: () => get<IGetCalendarTripsResponse>(`/trip/${year}/${week}`),
  });

  const getCalendarTrips = useMutation(
    (times: any) => get(`/trip/${times.year}/${times.week}`),
    {
      onSuccess: (data: any, variables: any, context: any) => {
        return data;
      },
    }
  );

  const create = useMutation((body: any) => post(`/trip`, body), {
    onSuccess: () => {
      queryClient.invalidateQueries(["trips", year, week]);
    },
  });

  const update = useMutation(
    (body: any) => patch(`/trip/${body.tripId}/drop`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["trips", year, week]);
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  const updateTimeslot = useMutation(
    (body: any) => put(`/trip/${body.tripId}/timeslot`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["trips", year, week]);
      },
    }
  );

  const updateDriver = useMutation(
    (body: any) =>
      patch(`/trip/${body.tripId}/assignTripManager/${body.driverId}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["trips", year, week]);
      },
    }
  );

  const removeDriver  = useMutation(
    (body: any) =>
      patch(`/trip/${body.tripId}/unassign`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["trips", year, week]);
      },
    }
  );

  const remove = useMutation((id: string) => del(`/trip/${id}`), {
    onSuccess: () => {
      queryClient.invalidateQueries(["trips", year, week]);
    },
  });

  // Preview an optimised plan for the day's trip(s). Read-only; the result is held
  // in component state until the user applies it.
  const optimise = useMutation(
    (body: IOptimiseTripsRequest): Promise<IOptimiseResultResponse> =>
      post(`/trip/optimise`, body)
  );

  // Commit an optimised plan: the backend re-orders the leads and saves each
  // trip's start time, so we refresh both trips and leads. The response carries
  // the prior state, which can be posted straight back here to undo.
  const applyOptimise = useMutation(
    (body: IApplyOptimiseRequest): Promise<IApplyOptimiseResponse> =>
      post(`/trip/optimise/apply`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["trips", year, week]);
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  // Geocode an unroutable collection's postcode and save its coordinates so it
  // can join the route on the next optimise.
  const geocodeLead = useMutation(
    (leadId: string): Promise<IGeocodeLeadResponse> =>
      post(`/trip/optimise/geocode/${leadId}`, {}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  return {
    trips,
    getCalendarTrips,
    create,
    update,
    updateTimeslot,
    updateDriver,
    removeDriver,
    remove,
    optimise,
    applyOptimise,
    geocodeLead,
  };
}
